import { useEffect, useState } from 'preact/hooks';
import type { PhoneApp } from '../app/phone.js';
import { Banners, InstallButton, MicDeniedCard, RejoinCard, RingCard, SoundStrip, Toasts } from './cards.js';
import { CallCard } from './CallCard.js';
import { DevicesPanel } from './DevicesPanel.js';
import { MissedView } from './MissedView.js';
import { SelfCheck } from './SelfCheck.js';
import { bindShortcuts } from './shortcuts.js';
import { formatAuNumber } from './format.js';

const PRESENCE = ['available', 'away', 'business_trip', 'do_not_disturb', 'lunch_break', 'off_work'] as const;

function StatusPill({ app }: { app: PhoneApp }) {
  const sip = app.sipState.value;
  const node = app.store.nodeState.value;
  const tone = node !== 'up' ? 'red' : sip === 'registered' ? 'green' : sip === 'failed' ? 'red' : 'amber';
  const label = node !== 'up' ? node : sip;
  return (
    <span class={`pill ${tone}`} role="status" aria-live="polite" data-testid="sip-status" data-sip={sip} data-node={node}>
      {label} · ext {app.agent.extension}
    </span>
  );
}

function Dialler({ app }: { app: PhoneApp }) {
  const [v, setV] = useState('');
  const req = app.router.dialRequest.value;
  return (
    <section class="card" aria-label="Dial">
      {req ? (
        <div class="row" role="status">
          <span class="chip">Call {formatAuNumber(req)}?</span>
          <button
            class="primary"
            onClick={() => {
              app.router.dialRequest.value = null;
              void app.dial(req);
            }}
          >
            Call
          </button>
          <button
            onClick={() => {
              app.router.dialRequest.value = null;
            }}
          >
            Cancel
          </button>
        </div>
      ) : null}
      <form
        class="row grow"
        onSubmit={(e) => {
          e.preventDefault();
          void app.dial(v).then(() => setV(''));
        }}
      >
        <input aria-label="Number to dial" data-testid="dial-input" inputMode="tel" placeholder="Number or extension" value={v} onInput={(e) => setV((e.target as HTMLInputElement).value)} disabled={app.clickToDialDisabled.value} />
        <button type="submit" class="primary" data-testid="dial-button" disabled={app.clickToDialDisabled.value || app.pendingKinds.value.has('dial')}>
          Dial {app.pendingKinds.value.has('dial') ? <span class="spinner" /> : null}
        </button>
      </form>
    </section>
  );
}

export function PhoneView({ app }: { app: PhoneApp }) {
  const [panel, setPanel] = useState<'none' | 'devices'>('none');
  useEffect(
    () =>
      bindShortcuts(
        document,
        () => app.lines.value,
        { answer: (id) => void app.answer(id), decline: (id) => void app.decline(id), mute: (id, on) => void app.mute(id, on), hold: (id, on) => void app.hold(id, on) },
      ),
    [app],
  );
  const route = app.router.route.value;
  const lines = app.lines.value;
  const ringing = lines.filter((l) => l.state === 'ringing');
  const others = lines.filter((l) => l.state !== 'ringing');
  const held = app.store.heldBridges.value;
  const s = app.store.state.value;
  return (
    <div class="app" data-testid="phone" data-relay={app.relayStatus.value} data-sip={app.sipState.value}>
      <header class="header">
        <span class="title">CommsChannel Phone</span>
        <StatusPill app={app} />
        <select aria-label="Presence" value={s.presence ?? 'available'} onChange={(e) => void app.setPresence((e.target as HTMLSelectElement).value as (typeof PRESENCE)[number])}>
          {PRESENCE.map((p) => (
            <option key={p} value={p}>
              {p.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
        <button aria-pressed={s.pausedQueues.length > 0} onClick={() => void app.pauseQueues(s.pausedQueues.length === 0)}>
          {s.pausedQueues.length > 0 ? 'Unpause' : 'Pause'}
        </button>
        <InstallButton app={app} />
        <button onClick={() => app.router.navigate(route.name === 'missed' ? '/' : '/missed')}>Missed{s.missed.length ? ` (${s.missed.length})` : ''}</button>
        <button onClick={() => app.router.navigate(route.name === 'self-check' ? '/' : '/self-check')}>Self-check</button>
        <button onClick={() => setPanel(panel === 'devices' ? 'none' : 'devices')} aria-expanded={panel === 'devices'}>
          Devices
        </button>
        <button class="ghost" data-testid="sign-out" onClick={() => void app.logout()}>
          Sign out
        </button>
      </header>
      <SoundStrip app={app} />
      <Banners app={app} />
      <main class="main">
        <MicDeniedCard app={app} />
        {ringing.map((l) => (
          <RingCard key={l.id} app={app} line={l} />
        ))}
        {route.name === 'missed' ? <MissedView app={app} /> : null}
        {route.name === 'self-check' ? <SelfCheck app={app} /> : null}
        {panel === 'devices' ? <DevicesPanel app={app} /> : null}
        {others.map((l) => (
          <CallCard key={l.id} app={app} line={l} />
        ))}
        {held.map((h) => (
          <RejoinCard key={h.bridge_id} app={app} bridge={h} />
        ))}
        {route.name === 'phone' ? <Dialler app={app} /> : null}
      </main>
      <footer class="footer">
        <span>{app.agent.display_name}</span>
        <span>{app.agent.tenant_slug}</span>
        <span>
          <span class="kbd">Enter</span> answer · <span class="kbd">Esc</span> decline · <span class="kbd">Ctrl+Shift+M</span> mute · <span class="kbd">Ctrl+Shift+H</span> hold
        </span>
        {app.install.installed.value ? null : <span>Install the phone and enable run on login (chrome://apps) so it is open for the whole shift.</span>}
      </footer>
      <Toasts app={app} />
    </div>
  );
}
